/**
 * UTC date helpers for the read heatmap and the analytics chart.
 *
 * ── UTC ONLY ────────────────────────────────────────────────────────────
 * The server buckets both series with `to_char(occurred_at AT TIME ZONE 'UTC')`.
 * If the client laid those buckets out in local time the whole calendar would
 * shift by a day for anyone west of UTC, and the grid would disagree with its
 * own tooltip. Hence the getUTC accessors and Date.UTC throughout, never
 * getMonth() or getDate().
 *
 * Shared by two views, which is why it is its own module: the heatmap and the
 * line chart MUST agree about which day a thing belongs to, and the cheapest
 * way to guarantee that is one implementation.
 */

export const WEEKS = 53
export const DAYS_PER_WEEK = 7
export const WINDOW_DAYS = WEEKS * DAYS_PER_WEEK

/** Column headers, where horizontal space is 11px per week. */
export const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

/**
 * Spoken form, for cell and point labels. Full words on purpose: a screen
 * reader pronouncing "Aug" is not what a person listening to a date expects,
 * and these labels are the only way the visuals are legible without sight.
 */
const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

/** `YYYY-MM-DD` for a Date, in UTC. */
export function utcDay(d: Date): string {
  return d.toISOString().slice(0, 10)
}

/**
 * Midnight UTC today.
 *
 * `now` is injectable so every date-dependent test can pin a fixed day rather
 * than drift with the calendar; the default reads the clock via Date.now(),
 * which is what ST-no-direct-date asks for in SPA code.
 */
export function todayUTC(now: Date = new Date(Date.now())): Date {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
}

export function addDays(d: Date, n: number): Date {
  return new Date(d.getTime() + n * 86_400_000)
}

/** "12 August 2026" — spelled out, because that is what a screen reader reads. */
export function longDate(day: string): string {
  const [y, m, d] = day.split('-').map(Number)
  if (!y || !m || !d) return day
  return `${d} ${MONTHS_LONG[m - 1]} ${y}`
}

/**
 * First cell of the grid: the Sunday of the week that is WEEKS-1 weeks before
 * the CURRENT week.
 *
 * Anchored to the current week, not to `today - WINDOW_DAYS`. Rewinding a raw
 * day offset to the preceding Sunday shifts the whole grid backwards by up to
 * six days, so the last column ends before today and the most recent reads are
 * simply not drawn — the grid silently goes stale by up to a week. Anchoring
 * here makes the final column the current week, with the days after today
 * rendered as holes.
 */
export function gridStart(today: Date = todayUTC()): Date {
  const startOfThisWeek = addDays(today, -today.getUTCDay())
  return addDays(startOfThisWeek, -(WEEKS - 1) * DAYS_PER_WEEK)
}
